import { Inject, Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { EnvToken } from '@app/core/env.tokens';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class PosthogService {
  protected readonly env = inject(EnvToken);

  private posthog: any;
  private flagOverrides = new Set<string>();

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private ngZone: NgZone,
  ) {}

  async init(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.parseFlagOverrides();

    const posthogModule = await import('posthog-js');
    this.posthog = posthogModule.default;

    this.ngZone.runOutsideAngular(() => {
      if (!this.env.posthog) {
        return;
      }
      this.posthog.init(this.env.posthog.key, {
        api_host: this.env.posthog.host,
        autocapture: { dom_event_allowlist: ['click', 'change', 'submit'] },
        capture_pageview: false,
        capture_pageleave: true,
        scroll_root_selector: '.handyhand_root_container',
      });
    });
  }

  /** Reads `?ph_flags=flag-a,flag-b` from the URL and stores them as overrides. */
  private parseFlagOverrides(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const raw = params.get('ph_flags');
    if (!raw) return;

    for (const flag of raw.split(',')) {
      const trimmed = flag.trim();
      if (trimmed) this.flagOverrides.add(trimmed);
    }
  }

  trackPageview(url: string): void {
    this.posthog?.capture('$pageview', { $current_url: url });
  }

  trackEvent(name: string, properties?: Record<string, any>): void {
    this.posthog?.capture(name, properties ?? {});
  }

  onFeatureFlags(callback: () => void): void {
    if (this.flagOverrides.size > 0) {
      callback();
    }
    this.posthog?.onFeatureFlags(callback);
  }

  /**
   * True when the flag is on. ⚠️ Boolean flags only — for a multivariate
   * experiment flag every assigned variant is a truthy string, so this
   * returns `true` for the CONTROL group too. Gate experiment variants with
   * `getFeatureFlag(key) === '<variant>'` instead.
   */
  isFeatureEnabled(flagKey: string): boolean {
    if (this.flagOverrides.has(flagKey)) return true;
    return this.posthog?.isFeatureEnabled(flagKey) ?? false;
  }

  /**
   * The flag's value: a variant key for multivariate (experiment) flags,
   * `true`/`false` for boolean flags, `undefined` while unresolved.
   * A `?ph_flags=` override resolves to the `test` variant.
   */
  getFeatureFlag(flagKey: string): string | boolean | undefined {
    if (this.flagOverrides.has(flagKey)) return 'test';
    return this.posthog?.getFeatureFlag(flagKey);
  }
}
